import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  CheckCircle2,
  Clock3,
  Eye,
  Loader2,
  PencilLine,
  Plus,
  QrCode,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import type {
  CalidadNcpAccionCorrectiva,
  CalidadNcpFicha,
  CalidadNcpFirma,
  CalidadNcpFirmaTipo,
} from "../types/calidad";
import {
  createEmptyCalidadNcpAction,
  formatCalidadDate,
  mapFichaToPayload,
} from "../types/calidad";
import {
  useCanManageCertificaciones,
  useCanSignNcpDireccion,
  useCanSignNcpResponsable,
} from "../../../../../shared/permissions/useCertificationPermissions";
import { CertificationButton } from "../../../../../shared/ui/CertificationButton";
import { CertificationModal } from "../../../../../shared/ui/CertificationModal";
import {
  createNcpSignatureRequest,
  getNcpSignatureRequestProgress,
  type NcpSignatureRequest,
} from "../../../../../shared/ncp-signature/api/ncpSignature";

type Props = {
  ficha: CalidadNcpFicha | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveError: string | null;
  onBack: () => void;
  onRetry: () => void;
  onSave: (ficha: CalidadNcpFicha) => Promise<void>;
};

type ViewMode = "editor" | "preview";

export function CalidadNcpFichaView({
  ficha,
  loading,
  saving,
  error,
  saveError,
  onBack,
  onRetry,
  onSave,
}: Props) {
  const canManageCertificaciones = useCanManageCertificaciones();
  const canSignResponsable = useCanSignNcpResponsable();
  const canSignDireccion = useCanSignNcpDireccion();
  const [viewMode, setViewMode] = useState<ViewMode>("preview");
  const [draft, setDraft] = useState<CalidadNcpFicha | null>(ficha);
  const [localError, setLocalError] = useState<string | null>(null);
  const [signatureRequest, setSignatureRequest] = useState<NcpSignatureRequest | null>(null);
  const [requestingSignature, setRequestingSignature] = useState<CalidadNcpFirmaTipo | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [signatureCompleted, setSignatureCompleted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const onRetryRef = useRef(onRetry);

  useEffect(() => {
    onRetryRef.current = onRetry;
  }, [onRetry]);

  useEffect(() => {
    setDraft(ficha);
    setLocalError(null);
    setViewMode("preview");
  }, [ficha]);

  useEffect(() => {
    if (!signatureRequest?.expires_at || signatureCompleted) {
      setRemainingSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil((new Date(signatureRequest.expires_at as string).getTime() - Date.now()) / 1000));
      setRemainingSeconds(remaining);
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [signatureRequest?.expires_at, signatureCompleted]);

  useEffect(() => {
    if (!signatureRequest || signatureCompleted || signatureRequest.status !== "pendiente" || !draft) {
      return;
    }

    let active = true;
    const poll = async () => {
      try {
        const progress = await getNcpSignatureRequestProgress(draft.incidencia_id, signatureRequest.id);
        if (!active) return;

        if (progress.status !== "pendiente") {
          setSignatureRequest((current) => current ? { ...current, status: progress.status } : current);
        }

        if (progress.status === "firmada") {
          setSignatureCompleted(true);
          setSignatureError(null);
          onRetryRef.current();
        } else if (progress.status === "caducada") {
          setSignatureError("El código QR ha caducado. Cierra esta ventana y genera uno nuevo.");
        } else if (progress.status === "invalidada") {
          setSignatureError("La solicitud ha sido invalidada. Guarda el informe y genera un nuevo QR.");
        }
      } catch {
        if (active) setSignatureError("No se pudo comprobar el estado de la firma.");
      }
    };

    void poll();
    const interval = window.setInterval(() => void poll(), 2000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [draft, signatureCompleted, signatureRequest]);

  const acciones = draft?.acciones_correctivas ?? [];
  const canRender = useMemo(() => draft !== null, [draft]);
  const hasUnsavedChanges = useMemo(() => {
    if (!draft || !ficha) return false;
    return JSON.stringify(mapFichaToPayload(draft)) !== JSON.stringify(mapFichaToPayload(ficha));
  }, [draft, ficha]);

  if (loading && !canRender) {
    return (
      <section className="calidad-document-shell">
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">Cargando ficha NCp</div>
          <p className="calidad-feedback-copy">
            Estamos preparando el informe de no conformidad y acción correctiva.
          </p>
        </div>
      </section>
    );
  }

  if (error && !canRender) {
    return (
      <section className="calidad-document-shell">
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">
            No se pudo abrir la ficha NCp
          </div>
          <p className="calidad-feedback-copy">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="calidad-feedback-action"
          >
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  if (!draft) {
    return null;
  }

  const updateAction = (
    index: number,
    patch: Partial<CalidadNcpAccionCorrectiva>,
  ) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        acciones_correctivas: current.acciones_correctivas.map(
          (accion, currentIndex) =>
            currentIndex === index ? { ...accion, ...patch } : accion,
        ),
      };
    });
  };

  const addAction = () => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        acciones_correctivas: [
          ...current.acciones_correctivas,
          createEmptyCalidadNcpAction(current.acciones_correctivas.length + 1),
        ],
      };
    });
  };

  const removeAction = (index: number) => {
    setDraft((current) => {
      if (!current || current.acciones_correctivas.length <= 1) {
        return current;
      }

      return {
        ...current,
        acciones_correctivas: current.acciones_correctivas
          .filter((_, currentIndex) => currentIndex !== index)
          .map((accion, currentIndex) => ({
            ...accion,
            orden: currentIndex + 1,
          })),
      };
    });
  };

  const saveDraft = async () => {
    setLocalError(null);

    if (draft.acciones_correctivas.length === 0) {
      setLocalError("La ficha debe incluir al menos una acción correctiva.");
      return;
    }

    await onSave(draft);
  };

  const startSignature = async (tipo: CalidadNcpFirmaTipo) => {
    setRequestingSignature(tipo);
    setSignatureError(null);
    setSignatureCompleted(false);

    try {
      const created = await createNcpSignatureRequest(draft.incidencia_id, tipo);
      setSignatureRequest(created);
    } catch (requestError) {
      setSignatureError(readSignatureError(requestError));
    } finally {
      setRequestingSignature(null);
    }
  };

  const closeSignatureModal = () => {
    setSignatureRequest(null);
    setSignatureError(null);
    setSignatureCompleted(false);
    setRemainingSeconds(0);
  };

  return (
    <>
    <section className="calidad-document-shell">
      <div className="calidad-document-topbar">
        <div>
          <div className="calidad-section-eyebrow">Ficha documental</div>
          <h2 className="calidad-section-title">Informe NCp</h2>
          <p className="calidad-section-copy">
            La ficha se abre por defecto en vista previa y replica la estructura
            del documento de auditoría para lectura y revisión.
          </p>
        </div>

        <div className="calidad-document-topbar-actions">
          <div className="calidad-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("preview")}
              className={viewMode === "preview" ? "is-active" : ""}
            >
              <Eye size={15} />
              Vista previa
            </button>
            {canManageCertificaciones ? (
              <button
                type="button"
                onClick={() => setViewMode("editor")}
                className={viewMode === "editor" ? "is-active" : ""}
              >
                <PencilLine size={15} />
                Formulario editable
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onBack}
            className="calidad-back-button"
          >
            Volver
          </button>

          <button
            type="button"
            onClick={() => {
              void saveDraft();
            }}
            disabled={saving}
            className="calidad-primary-button"
          >
            <Save size={16} />
            {saving ? "Guardando..." : "Guardar ficha"}
          </button>
        </div>
      </div>

      {(localError || saveError) && (
        <div className="calidad-inline-error">{localError ?? saveError}</div>
      )}

      {signatureError && !signatureRequest ? (
        <div className="calidad-inline-error">{signatureError}</div>
      ) : null}

      {viewMode === "editor" && canManageCertificaciones ? (
        <div className="calidad-document-page">
          <section className="calidad-document-section">
            <div className="calidad-document-section-title">NO CONFORMIDAD</div>

            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock label="Número">
                <input
                  value={draft.numero ?? ""}
                  readOnly
                  className="field-control cursor-not-allowed bg-slate-100"
                />
              </FieldBlock>

              <FieldBlock label="Fecha">
                <input
                  value={formatCalidadDate(draft.fecha)}
                  readOnly
                  className="field-control cursor-not-allowed bg-slate-100"
                />
              </FieldBlock>

              <FieldBlock
                label="Descripción de la incidencia"
                className="md:col-span-2"
              >
                <textarea
                  value={draft.descripcion_incidencia ?? ""}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            descripcion_incidencia: event.target.value,
                          }
                        : current,
                    )
                  }
                  className="field-control min-h-[150px]"
                />
              </FieldBlock>

              <FieldBlock label="Acción inmediata" className="md:col-span-2">
                <textarea
                  value={draft.accion_inmediata ?? ""}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? { ...current, accion_inmediata: event.target.value }
                        : current,
                    )
                  }
                  className="field-control min-h-[120px]"
                />
              </FieldBlock>

              <FieldBlock label="Responsable de la acción inmediata">
                <input
                  value={draft.responsable_accion_inmediata ?? ""}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            responsable_accion_inmediata: event.target.value,
                          }
                        : current,
                    )
                  }
                  className="field-control"
                />
              </FieldBlock>

              <FieldBlock label="Plazo de la acción inmediata">
                <input
                  value={draft.plazo_accion_inmediata ?? ""}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            plazo_accion_inmediata: event.target.value,
                          }
                        : current,
                    )
                  }
                  className="field-control"
                />
              </FieldBlock>

              <FieldBlock
                label="Comprobación de la solución"
                className="md:col-span-2"
              >
                <textarea
                  value={draft.comprobacion_solucion ?? ""}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            comprobacion_solucion: event.target.value,
                          }
                        : current,
                    )
                  }
                  className="field-control min-h-[120px]"
                />
              </FieldBlock>

              <FieldBlock
                label="¿Requiere acción correctiva?"
                className="md:col-span-2"
              >
                <div className="calidad-boolean-group">
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((current) =>
                        current
                          ? { ...current, requiere_accion_correctiva: true }
                          : current,
                      )
                    }
                    className={
                      draft.requiere_accion_correctiva === true
                        ? "is-active"
                        : ""
                    }
                  >
                    Sí
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((current) =>
                        current
                          ? { ...current, requiere_accion_correctiva: false }
                          : current,
                      )
                    }
                    className={
                      draft.requiere_accion_correctiva === false
                        ? "is-active"
                        : ""
                    }
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((current) =>
                        current
                          ? { ...current, requiere_accion_correctiva: null }
                          : current,
                      )
                    }
                    className={
                      draft.requiere_accion_correctiva === null
                        ? "is-active"
                        : ""
                    }
                  >
                    Sin indicar
                  </button>
                </div>
              </FieldBlock>
            </div>
          </section>

          {acciones.map((accion, index) => (
            <section
              key={`${accion.id}-${index}`}
              className="calidad-document-section"
            >
              <div className="calidad-action-header">
                <div className="calidad-document-section-title">
                  {`ACCIÓN CORRECTIVA ${index + 1}`}
                </div>

                <button
                  type="button"
                  onClick={() => removeAction(index)}
                  disabled={acciones.length <= 1}
                  className="calidad-delete-inline"
                >
                  <Trash2 size={15} />
                  Eliminar bloque
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FieldBlock
                  label="Análisis de la causa"
                  className="md:col-span-2"
                >
                  <textarea
                    value={accion.analisis_causa ?? ""}
                    onChange={(event) =>
                      updateAction(index, {
                        analisis_causa: event.target.value,
                      })
                    }
                    className="field-control min-h-[120px]"
                  />
                </FieldBlock>

                <FieldBlock
                  label="¿Cómo lo vamos a solucionar?"
                  className="md:col-span-2"
                >
                  <textarea
                    value={accion.como_solucionar ?? ""}
                    onChange={(event) =>
                      updateAction(index, {
                        como_solucionar: event.target.value,
                      })
                    }
                    className="field-control min-h-[140px]"
                  />
                </FieldBlock>

                <FieldBlock label="Responsable">
                  <input
                    value={accion.responsable ?? ""}
                    onChange={(event) =>
                      updateAction(index, { responsable: event.target.value })
                    }
                    className="field-control"
                  />
                </FieldBlock>

                <FieldBlock label="Plazo">
                  <input
                    value={accion.plazo ?? ""}
                    onChange={(event) =>
                      updateAction(index, { plazo: event.target.value })
                    }
                    className="field-control"
                  />
                </FieldBlock>

                <FieldBlock
                  label="Evaluación de la eficacia"
                  className="md:col-span-2"
                >
                  <textarea
                    value={accion.evaluacion_eficacia ?? ""}
                    onChange={(event) =>
                      updateAction(index, {
                        evaluacion_eficacia: event.target.value,
                      })
                    }
                    className="field-control min-h-[150px]"
                  />
                </FieldBlock>
              </div>
            </section>
          ))}

          <button
            type="button"
            onClick={addAction}
            className="calidad-add-action"
          >
            <Plus size={16} />
            Añadir acción correctiva
          </button>

          <section className="calidad-document-section">
            <div className="calidad-document-section-title">FIRMAS</div>

            <p className="calidad-signatures-note">
              Las firmas se realizan sobre la versión guardada del informe. Guarda los cambios
              y utiliza la vista previa para generar el código QR de firma.
            </p>

            <SignatureCards
              ficha={draft}
              canSignResponsable={canSignResponsable}
              canSignDireccion={canSignDireccion}
              requestingSignature={requestingSignature}
              allowActions={false}
              canStartSignatures={!hasUnsavedChanges}
              onSign={startSignature}
            />
            <SignatureHistory firmas={draft.firmas} />
          </section>
        </div>
      ) : (
        <div className="calidad-document-page calidad-document-preview">
          <div className="calidad-word-sheet">
            <PreviewNoConformidadBlock ficha={draft} />

            {acciones.map((accion, index) => (
              <PreviewAccionCorrectivaBlock
                key={`${accion.id}-${index}`}
                accion={accion}
                index={index}
              />
            ))}

            <PreviewFirmasBlock
              ficha={draft}
              canSignResponsable={canSignResponsable}
              canSignDireccion={canSignDireccion}
              requestingSignature={requestingSignature}
              canStartSignatures={!hasUnsavedChanges}
              onSign={startSignature}
            />
          </div>
        </div>
      )}
    </section>

      <CertificationModal
        open={signatureRequest !== null}
        title={signatureCompleted ? "Firma completada" : "Firmar Informe NCp con QR"}
        eyebrow="Firma segura"
        description={signatureRequest ? `${signatureRequest.tipo_label} · versión ${signatureRequest.documento_version}` : undefined}
        onClose={closeSignatureModal}
        closeOnBackdrop={false}
        footer={
          <CertificationButton variant="secondary" onClick={closeSignatureModal}>
            Cerrar
          </CertificationButton>
        }
      >
        {signatureCompleted ? (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-emerald-800">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={24} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-black">Firma registrada correctamente</div>
                <p className="mt-1 text-sm font-semibold leading-6">
                  El informe se ha actualizado con la identidad del firmante, la fecha, la versión y la huella del documento.
                </p>
              </div>
            </div>
          </div>
        ) : signatureRequest ? (
          <div className="space-y-4">
            <div className="mx-auto w-fit rounded-[28px] border border-emerald-100 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,.08)]">
              <img
                src={signatureRequest.qr_data_uri}
                alt={`Código QR para firmar como ${signatureRequest.tipo_label}`}
                className="h-64 w-64 sm:h-72 sm:w-72"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold leading-6 text-slate-600">
              <div className="flex items-center gap-2 font-black text-slate-800">
                <QrCode size={17} /> Escanea el código con el móvil
              </div>
              <p className="mt-2">
                En el móvil se solicitará el correo corporativo del firmante y un código temporal enviado a ese mismo correo.
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-emerald-700">
                <Clock3 size={14} />
                {remainingSeconds > 0 ? `Caduca en ${formatRemaining(remainingSeconds)}` : "Comprobando caducidad"}
              </div>
            </div>

            {signatureError ? (
              <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
                {signatureError}
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm font-bold text-emerald-700">
                <Loader2 size={16} className="animate-spin" /> Esperando validación desde el móvil...
              </div>
            )}
          </div>
        ) : null}
      </CertificationModal>
    </>
  );
}

function FieldBlock({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`.trim()}>
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function PreviewNoConformidadBlock({ ficha }: { ficha: CalidadNcpFicha }) {
  return (
    <section className="calidad-word-block">
      <div className="calidad-word-header-grid">
        <div className="calidad-word-header-main">NO CONFORMIDAD</div>
        <div className="calidad-word-header-meta">
          <span>Nº</span>
          <strong>{ficha.numero ?? "-"}</strong>
        </div>
        <div className="calidad-word-header-meta">
          <span>FECHA</span>
          <strong>{formatCalidadDate(ficha.fecha)}</strong>
        </div>
      </div>

      <WordLabelRow
        label="Descripción de la incidencia"
        value={ficha.descripcion_incidencia ?? ficha.literal}
      />

      <div className="calidad-word-section-ribbon">ACCIÓN INMEDIATA</div>
      <WordTextBody value={ficha.accion_inmediata} minHeight="88px" />

      <div className="calidad-word-split-row">
        <WordCell label="Responsable" value={ficha.responsable_accion_inmediata} />
        <WordCell label="Plazo" value={ficha.plazo_accion_inmediata} narrow />
      </div>

      <WordLabelRow
        label="Comprobación de la solución"
        value={ficha.comprobacion_solucion}
      />

      <WordLabelRow
        label="¿Requiere acción correctiva?"
        value={formatBooleanAnswer(ficha.requiere_accion_correctiva)}
      />
    </section>
  );
}

function PreviewAccionCorrectivaBlock({
  accion,
  index,
}: {
  accion: CalidadNcpAccionCorrectiva;
  index: number;
}) {
  return (
    <section className="calidad-word-block">
      <div className="calidad-word-header-full">{`ACCIÓN CORRECTIVA ${index + 1}`}</div>

      <WordLabelRow
        label="Análisis de la causa"
        value={accion.analisis_causa}
      />

      <WordLabelRow
        label="¿Cómo lo vamos a solucionar?"
        value={accion.como_solucionar}
      />

      <div className="calidad-word-split-row">
        <WordCell label="Responsable" value={accion.responsable} />
        <WordCell label="Plazo" value={accion.plazo} narrow />
      </div>

      <WordLabelRow
        label="Evaluación de la eficacia"
        value={accion.evaluacion_eficacia}
      />
    </section>
  );
}

function PreviewFirmasBlock({
  ficha,
  canSignResponsable,
  canSignDireccion,
  requestingSignature,
  canStartSignatures,
  onSign,
}: {
  ficha: CalidadNcpFicha;
  canSignResponsable: boolean;
  canSignDireccion: boolean;
  requestingSignature: CalidadNcpFirmaTipo | null;
  canStartSignatures: boolean;
  onSign: (tipo: CalidadNcpFirmaTipo) => Promise<void>;
}) {
  return (
    <section className="calidad-word-block">
      <div className="calidad-word-header-full">FIRMAS</div>

      <SignatureCards
        ficha={ficha}
        canSignResponsable={canSignResponsable}
        canSignDireccion={canSignDireccion}
        requestingSignature={requestingSignature}
        allowActions
        canStartSignatures={canStartSignatures}
        onSign={onSign}
      />

      {!canStartSignatures ? (
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">
          Hay cambios sin guardar. Guarda la ficha antes de generar un QR para que la firma corresponda exactamente al documento mostrado.
        </div>
      ) : null}

      <SignatureHistory firmas={ficha.firmas} />

      <div className="calidad-word-footnote">
        Las firmas se validan mediante código QR y un código temporal enviado al correo corporativo del firmante.
        Si cambia el contenido del informe, las firmas vigentes quedan invalidadas y deben renovarse.
      </div>
    </section>
  );
}

function SignatureCards({
  ficha,
  canSignResponsable,
  canSignDireccion,
  requestingSignature,
  allowActions,
  canStartSignatures,
  onSign,
}: {
  ficha: CalidadNcpFicha;
  canSignResponsable: boolean;
  canSignDireccion: boolean;
  requestingSignature: CalidadNcpFirmaTipo | null;
  allowActions: boolean;
  canStartSignatures: boolean;
  onSign: (tipo: CalidadNcpFirmaTipo) => Promise<void>;
}) {
  return (
    <div className="calidad-word-signatures">
      <SignatureCard
        label="Responsable del Sistema Integrado"
        tipo="responsable_si"
        firma={findActiveSignature(ficha, "responsable_si")}
        legacyMarked={ficha.firma_responsable_legacy}
        canSign={canSignResponsable}
        requestingSignature={requestingSignature}
        allowActions={allowActions}
        canStartSignatures={canStartSignatures}
        onSign={onSign}
      />
      <SignatureCard
        label="Dirección"
        tipo="direccion"
        firma={findActiveSignature(ficha, "direccion")}
        legacyMarked={ficha.firma_direccion_legacy}
        canSign={canSignDireccion}
        requestingSignature={requestingSignature}
        allowActions={allowActions}
        canStartSignatures={canStartSignatures}
        onSign={onSign}
      />
    </div>
  );
}

function SignatureCard({
  label,
  tipo,
  firma,
  legacyMarked,
  canSign,
  requestingSignature,
  allowActions,
  canStartSignatures,
  onSign,
}: {
  label: string;
  tipo: CalidadNcpFirmaTipo;
  firma: CalidadNcpFirma | null;
  legacyMarked: boolean;
  canSign: boolean;
  requestingSignature: CalidadNcpFirmaTipo | null;
  allowActions: boolean;
  canStartSignatures: boolean;
  onSign: (tipo: CalidadNcpFirmaTipo) => Promise<void>;
}) {
  const requesting = requestingSignature === tipo;

  return (
    <div className="calidad-word-signature-cell">
      <div className="calidad-word-signature-label">
        <ShieldCheck size={15} className={firma ? "text-[#1f5c13]" : "text-slate-300"} />
        {label}
      </div>

      {firma ? (
        <>
          <div className="calidad-word-signature-value text-emerald-800">Firmado electrónicamente</div>
          <div className="mt-2 text-sm font-black text-slate-800">{firma.usuario_nombre}</div>
          <div className="text-xs font-semibold text-slate-500">{firma.usuario_correo}</div>
          <div className="calidad-word-signature-date">{formatSignatureDate(firma.firmado_en)}</div>
          <div className="mt-2 text-[11px] font-bold text-slate-500">
            Versión {firma.documento_version} · QR + correo · Huella {shortHash(firma.documento_hash)}
          </div>
        </>
      ) : (
        <>
          {legacyMarked ? (
            <>
              <div className="calidad-word-signature-value text-amber-800">Marca histórica sin identidad verificada</div>
              <div className="calidad-word-signature-date">
                La ficha anterior tenía esta casilla marcada, pero no registraba quién la firmó. Debe renovarse mediante QR para considerarse una firma trazable.
              </div>
            </>
          ) : (
            <>
              <div className="calidad-word-signature-value">Pendiente de firma</div>
              <div className="calidad-word-signature-date">La firma se vinculará al usuario validado por correo.</div>
            </>
          )}
          {allowActions && canSign ? (
            <button
              type="button"
              onClick={() => void onSign(tipo)}
              disabled={requestingSignature !== null || !canStartSignatures}
              className="calidad-secondary-button mt-3"
            >
              {requesting ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />}
              {requesting ? "Generando QR..." : legacyMarked ? "Renovar firma con QR" : "Firmar con QR"}
            </button>
          ) : allowActions ? (
            <div className="mt-3 text-xs font-bold text-slate-400">No tienes permiso para realizar esta firma.</div>
          ) : null}
        </>
      )}
    </div>
  );
}

function SignatureHistory({ firmas }: { firmas: CalidadNcpFirma[] }) {
  if (firmas.length === 0) return null;

  return (
    <details className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <summary className="cursor-pointer text-sm font-black text-slate-700">
        Historial de firmas ({firmas.length})
      </summary>
      <div className="mt-3 space-y-2">
        {firmas.map((firma) => (
          <div key={firma.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
            <div className="font-black text-slate-800">
              {firma.tipo === "responsable_si" ? "Responsable SI" : "Dirección"} · {firma.usuario_nombre}
            </div>
            <div className="mt-1 font-semibold">
              {formatSignatureDate(firma.firmado_en)} · Versión {firma.documento_version} · {firma.vigente ? "Vigente" : "Invalidada"}
            </div>
            {!firma.vigente && firma.invalidacion_motivo ? (
              <div className="mt-1 text-amber-700">{firma.invalidacion_motivo}</div>
            ) : null}
          </div>
        ))}
      </div>
    </details>
  );
}

function findActiveSignature(ficha: CalidadNcpFicha, tipo: CalidadNcpFirmaTipo): CalidadNcpFirma | null {
  return ficha.firmas.find((firma) => firma.tipo === tipo && firma.vigente) ?? null;
}

function WordLabelRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="calidad-word-row-block">
      <div className="calidad-word-row-label">{label}</div>
      <WordTextBody value={value} />
    </div>
  );
}

function WordTextBody({
  value,
  minHeight = "74px",
}: {
  value: string | null | undefined;
  minHeight?: string;
}) {
  return (
    <div className="calidad-word-row-body" style={{ minHeight }}>
      {value?.trim() ? value : "Pendiente de completar"}
    </div>
  );
}

function WordCell({
  label,
  value,
  narrow = false,
}: {
  label: string;
  value: string | null | undefined;
  narrow?: boolean;
}) {
  return (
    <div className={`calidad-word-cell ${narrow ? "is-narrow" : ""}`.trim()}>
      <div className="calidad-word-cell-label">{label}</div>
      <div className="calidad-word-cell-value">
        {value?.trim() ? value : "Pendiente"}
      </div>
    </div>
  );
}

function formatBooleanAnswer(value: boolean | null) {
  if (value === true) {
    return "Sí";
  }

  if (value === false) {
    return "No";
  }

  return "Sin indicar";
}

function formatSignatureDate(value: string | null): string {
  if (!value) return "Fecha no disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortHash(value: string): string {
  if (value.length <= 16) return value;
  return `${value.slice(0, 8).toUpperCase()}…${value.slice(-8).toUpperCase()}`;
}

function formatRemaining(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function readSignatureError(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as {
      message?: string;
      errors?: Record<string, string[]>;
    } | undefined;
    const firstValidationError = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return firstValidationError ?? data?.message ?? "No se pudo iniciar la firma.";
  }

  return "No se pudo iniciar la firma.";
}

